import type { Reel } from './reelTypes';

export type AddedReelSection = {
  sectionId: string;
  /** The clip's own title, which seeds the Section's name (DE8). */
  title?: string;
};

/**
 * PLAT-11057 — records the names of Sections added to a reel.
 *
 * Names are seeded from the clip each Section came from, with
 * `nameSource: 'clip'` — replaceable by a generation (PLAT-11059) and by hand,
 * unlike a `'user'` name (DE10). A clip with no title contributes nothing, so
 * the Reel Editor falls back to the Section's opening words.
 *
 * **Chapters need no write at all.** Membership is positional (DE12), so a
 * Section inserted mid-reel joins the chapter it landed inside for free; and
 * for an insert at the very front, `deriveReelChapterGroups` already moves the
 * opening boundary onto whichever Section now comes first (PLAT-11055 DS3).
 * Reconciling there rather than here is the epic's rule — read, not write
 * (PLAT-11052 DS1).
 *
 * Pure and non-mutating — the caller writes the result into `clipJson.reel`.
 */
export function addSectionsToReel(
  reel: Reel | undefined,
  added: readonly AddedReelSection[],
): Reel {
  const sections = { ...reel?.sections };
  for (const { sectionId, title } of added) {
    const name = title?.trim();
    if (name) sections[sectionId] = { name, nameSource: 'clip' };
  }

  // `...reel` first, so anything else stored on it rides across rather than
  // being dropped by a writer that predates it (PLAT-11072 `disabledSections`).
  return { ...reel, chapters: reel?.chapters ?? [], sections };
}
